/**
 * Is the platform working?
 *
 * The honest version. Everything here is something the system observes about
 * itself — and what it cannot observe is named rather than approximated.
 *
 * **Firestore rule denials are not on this page.** They are a Cloud Monitoring
 * metric and no API hands them to a Cloud Function. What is here instead is the
 * count of CALLABLE refusals, which measures a related thing, and it is labelled
 * as what it is: a page saying "rule denials" while showing something else would
 * be worse than a page without them.
 */

export type JobState = "ok" | "failing" | "missed" | "never";

export interface JobRow {
  name: string;
  startedAt: string;
  finishedAt?: string;
  ok: boolean;
  durationMs: number;
  error?: string;
  consecutiveFailures: number;
  state: JobState;
  expectedEveryHours: number | undefined;
}

export type Metric =
  | "emailSent"
  | "emailFailed"
  | "webhookHandled"
  | "webhookFailed"
  | "callRefused";

export interface PlatformHealth {
  jobs: JobRow[];
  sessions: { active: number; online: number; offline: number };
  counts: Record<string, Partial<Record<Metric, number>>>;
  at: string;
}

export const JOB_STATE_KEY: Record<JobState, string> = {
  ok: "health.job.ok",
  failing: "health.job.failing",
  missed: "health.job.missed",
  never: "health.job.never",
};

export const JOB_STATE_TONE: Record<JobState, string> = {
  ok: "text-cue-green",
  failing: "text-accent",
  // A job that has never run and a job that has stopped running are both
  // absences, and an absence is what this page exists to make visible.
  missed: "text-accent",
  never: "text-accent",
};

const STATE_ORDER: Record<JobState, number> = { never: 0, missed: 1, failing: 2, ok: 3 };

export function sortJobs(jobs: readonly JobRow[]): JobRow[] {
  return [...jobs].sort(
    (a, b) => STATE_ORDER[a.state] - STATE_ORDER[b.state] || a.name.localeCompare(b.name)
  );
}

export function jobsNeedingAttention(jobs: readonly JobRow[]): JobRow[] {
  return jobs.filter((job) => job.state !== "ok");
}

/** A metric as a series, oldest first, filling the days nothing happened. */
export function series(
  counts: PlatformHealth["counts"],
  metric: Metric,
  days: number,
  today: string
): Array<{ day: string; value: number }> {
  const end = Date.parse(`${today.slice(0, 10)}T00:00:00.000Z`);

  return Array.from({ length: days }, (_, index) => {
    const day = new Date(end - (days - 1 - index) * 86_400_000).toISOString().slice(0, 10);
    return { day, value: counts[day]?.[metric] ?? 0 };
  });
}

export const SPIKE_FACTOR = 5;
export const SPIKE_FLOOR = 20;

/**
 * Whether today stands far enough above the recent norm to be worth a look.
 *
 * The same rule as the backend's, restated here so the page can colour a number
 * without a round trip. Compared against the median of the preceding days: one
 * bad day drags a mean up until the next one looks ordinary.
 */
export function isSpike(today: number, previous: readonly number[]): boolean {
  if (today < SPIKE_FLOOR) return false;
  if (previous.length === 0) return true;

  const sorted = [...previous].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  const median =
    sorted.length % 2 === 0 ? (sorted[middle - 1]! + sorted[middle]!) / 2 : sorted[middle]!;

  return median === 0 ? true : today >= median * SPIKE_FACTOR;
}

export function todayIsSpiking(
  counts: PlatformHealth["counts"],
  metric: Metric,
  today: string
): boolean {
  const points = series(counts, metric, 14, today);
  const last = points.at(-1);
  if (!last) return false;

  return isSpike(
    last.value,
    points.slice(0, -1).map((point) => point.value)
  );
}
